<template>
  <div ref="products-models-selector" class="products-models-selector">
    <div v-if="loading" class="loader"></div>
    <div v-else>
      <div class="products-models-selector-cnt">
        <div class="navbar-background">
          <nav v-if="allowableProductModels.length" class="jump-to-menu" data-cy="jump-to-menu">
            <div class="jump-to-main-groups">
              <button
                v-for="productMainGroupModel in mainGroupsProductModels"
                :key="productMainGroupModel.mainGroup"
                class="jump-to"
                :class="[{ selected: productMainGroupModel.selected }]"
                :data-cy="`jump-to-tab.${productMainGroupModel.mainGroup}`"
                :style="`background-image: url(${getJumpToImageUrl(productMainGroupModel.mainGroup)})`"
                v-on:click="scrollMeTo(productMainGroupModel.mainGroup)">
                {{
                  `${$t(`global.${productMainGroupModel.mainGroup}`)} ( ${getTotalSelectProductModelsForMainGroup(
                    productMainGroupModel.productModels,
                  )} )`
                }}
              </button>
              <input
                v-model="searchText"
                id="search-products"
                data-cy="search-bar"
                :style="{ width: getWidthSearchInput() }"
                class="search-input"
                type="text"
                :placeholder="getSearchPlaceholder()" />
            </div>
          </nav>
          <div
            v-if="allowableProductModels.length > 0 && visibleProducts.length === 0"
            class="no-prooducts-found-message"
            data-cy="no-products-found-message">
            {{ $t("matrix['no products were found while searching']") }}
          </div>
        </div>
        <div ref="products-models-list" class="products-models-list" data-cy="products-model-list">
          <div v-if="mainGroupsProductModels.length > 0">
            <section
              v-for="productMainGroupModel in mainGroupsProductModels"
              :id="`${productMainGroupModel.mainGroup}-section`"
              :key="productMainGroupModel.mainGroup"
              :data-cy="`main-group-tab.${productMainGroupModel.mainGroup}`"
              class="main-group-section">
              <div class="section-actions">
                <div
                  :ref="productMainGroupModel.mainGroup"
                  :data-main-group="productMainGroupModel.mainGroup"
                  class="section-title">
                  {{
                    `${$t(`global.${productMainGroupModel.mainGroup}`)} (${getTotalSelectProductModelsForMainGroup(
                      productMainGroupModel.productModels,
                    )})`
                  }}
                </div>
                <div class="section-buttons">
                  <BackstageButton
                    :disabled="searchText !== ''"
                    :variant="ButtonVariant.SECONDARY"
                    :data-cy="`select-all-button.${productMainGroupModel.mainGroup}-cnt`"
                    :data-cy-button="`select-all-button.${productMainGroupModel.mainGroup}`"
                    v-on:click="selectAllProductModels(productMainGroupModel.productModels)">
                    {{
                      $t('global.select_all_button_filter') +
                      ' ' +
                      $t(`global.${productMainGroupModel.mainGroup}`).toString().toLocaleLowerCase()
                    }}
                  </BackstageButton>
                  <BackstageButton
                    :disabled="searchText !== ''"
                    :variant="ButtonVariant.SECONDARY"
                    :data-cy="`clear-all-button.${productMainGroupModel.mainGroup}-cnt`"
                    :data-cy-button="`clear-all-button.${productMainGroupModel.mainGroup}`"
                    v-on:click="clearAllProductModels(productMainGroupModel.productModels)">
                    {{
                      $t('global.clear_all_button_filter') +
                      ' ' +
                      $t(`global.${productMainGroupModel.mainGroup}`).toString().toLocaleLowerCase()
                    }}
                  </BackstageButton>
                </div>
              </div>
              <div class="product-model-cards">
                <ProductModelCard
                  v-for="productModel in productMainGroupModel.productModels"
                  :key="productModel.name"
                  :product-model="productModel"
                  :class="[{ selected: productModel.selected }]"
                  @click="selectProductModel(productModel)"
                  @keyup.enter="selectProductModel(productModel)"
                  @showProductModelDetailsPopup="showProductModelDetailsPopup(productModel)" />
              </div>
            </section>
          </div>
          <div v-if="allowableProductModels.length === 0">
            {{ $t('matrix.no_assortment', { orderType }) }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="showProductModelDetails">
      <NotificationMessage :size="'full'" class="popup-new" v-on:close="closeProductModelDetailsPopup">
        <ViewProductModelDetails
          :hide-select-button="hideSelectButton"
          :product-model="showProductModelDetails"
          v-on:toggle="toggleShowProductModelDetailsSelected"
          v-on:close="closeProductModelDetailsPopup" />
      </NotificationMessage>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import ViewProductModelDetails from '@/components/products/ViewProductModelDetails.vue';
import { useBPLogger } from '@/composables/business-process-logger';
import { ButtonVariant } from '@/models/button-variant.enum';
import { OrderType, ProductMainGroups } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useFormat } from '../../composables/format';
import { ordersMixins } from '../../mixins/orders.mixins';
import { AllowableProductModel } from '../../models/allowable-product-model.model';
import { AllowableAssortmentService } from '../../services/allowable-assortment.service';
import { useUserStore } from '../../stores/user';
import ProductModelCard from './ProductModelCard.vue';

export default defineComponent({
  name: 'products-models-selector',
  components: {
    ViewProductModelDetails,
    NotificationMessage,
    BackstageButton,
    ProductModelCard,
  },
  props: {
    orderDate: {
      type: String,
      required: true,
    },
    orderNumber: {
      type: String,
      default: undefined,
    },
    preSelectedProductModelNames: {
      type: Array<string>,
      default: [],
    },
    orderType: {
      type: String,
      default: OrderType.ReOrder,
    },
    hideSelectButton: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { selectedAccountId } = storeToRefs(useUserStore());

    const { formatDateToIsoStringIgnoringTime } = useFormat();

    const { pageViewBPLog } = useBPLogger();

    return {
      accountId: selectedAccountId,
      formatDateToIsoStringIgnoringTime,
      ButtonVariant,
      pageViewBPLog,
    };
  },
  data() {
    return {
      loading: true,
      submitting: false,
      allowableAssortmentService: new AllowableAssortmentService(),
      allowableProductModels: new Array<AllowableProductModel>(),
      requestedDate: this.orderDate,
      searchText: '',
      selectedMainGroup: 'shoes',
      showProductModelDetails: undefined as AllowableProductModel | undefined,
      page_visit_start: 0,
    };
  },
  computed: {
    visibleProducts(): AllowableProductModel[] {
      if (this.searchText.trim() !== '') {
        const searchTextLowerCase = this.searchText.toLocaleLowerCase();

        return this.allowableProductModels.filter(
          (allowableProductModel) =>
            allowableProductModel.name.toLocaleLowerCase().includes(searchTextLowerCase) ||
            allowableProductModel.itemNumbers?.some((itemNumber: string) =>
              itemNumber.toLocaleLowerCase().startsWith(searchTextLowerCase),
            ),
        );
      }

      return this.allowableProductModels;
    },
    mainGroupsProductModels(): {
      mainGroup: ProductMainGroups;
      selected: boolean;
      productModels: AllowableProductModel[];
    }[] {
      let mainGroupsProductModels = [
        {
          mainGroup: ProductMainGroups.shoes,
          selected: this.selectedMainGroup === ProductMainGroups.shoes,
          productModels: new Array<AllowableProductModel>(),
        },
        {
          mainGroup: ProductMainGroups.apparel,
          selected: this.selectedMainGroup === ProductMainGroups.apparel,
          productModels: new Array<AllowableProductModel>(),
        },
        {
          mainGroup: ProductMainGroups.accessories,
          selected: this.selectedMainGroup === ProductMainGroups.accessories,
          productModels: new Array<AllowableProductModel>(),
        },
      ];

      this.visibleProducts.forEach((allowableProductModel) => {
        const allowableMainGroupsProductModel = mainGroupsProductModels.find(
          (mainGroupsProductModel) => mainGroupsProductModel.mainGroup === allowableProductModel.mainGroup,
        );

        if (allowableMainGroupsProductModel) {
          allowableMainGroupsProductModel.productModels =
            allowableMainGroupsProductModel.productModels.concat(allowableProductModel);
        }
      });

      mainGroupsProductModels = mainGroupsProductModels.filter(
        (mainGroupsProductModel) => mainGroupsProductModel.productModels.length,
      );

      return mainGroupsProductModels;
    },
  },
  watch: {
    accountId(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.fetchAllowableAssortment();
      }
    },
  },
  beforeMount() {
    this.fetchAllowableAssortment();
  },
  mounted() {
    this.page_visit_start = new Date().getTime();
  },
  unmounted() {
    const visitDuration = new Date().getTime() - this.page_visit_start;
    console.log(this.pageViewBPLog(true, { page_name: 'products-model-selector' }, visitDuration));
  },
  methods: {
    ...ordersMixins,
    getProductModelsForMainGroup(productMainGroup: ProductMainGroups) {
      return this.visibleProducts.filter(
        (allowableProductModel) => allowableProductModel.mainGroup === productMainGroup,
      );
    },
    selectProductModel(productModel: AllowableProductModel) {
      productModel['selected'] = !productModel.selected;
      this.emitSelectedProductModels();
    },
    getTotalSelectProductModelsForMainGroup(productModels: AllowableProductModel[]) {
      return productModels.filter((productModel) => productModel.selected).length;
    },
    selectAllProductModels(productModels: AllowableProductModel[]) {
      productModels.forEach((allowableProductModel) => {
        allowableProductModel['selected'] = true;
      });
      this.emitSelectedProductModels();
    },
    clearAllProductModels(productModels: AllowableProductModel[]) {
      productModels.forEach((allowableProductModel) => {
        allowableProductModel['selected'] = false;
      });
      this.emitSelectedProductModels();
    },
    emitSelectedProductModels() {
      this.$emit(
        'selected-product-models',
        this.allowableProductModels
          .filter((productModel) => productModel.selected)
          .map((productModel) => productModel.name),
      );
    },
    observeMainGroups() {
      this.$nextTick(() => {
        const shoes = this.$refs[ProductMainGroups.shoes] as HTMLElement[];
        const apparel = this.$refs[ProductMainGroups.apparel] as HTMLElement[];
        const accessories = this.$refs[ProductMainGroups.accessories] as HTMLElement[];

        if (accessories && accessories[0]) {
          this.getIntersectObserver().observe(accessories[0]);
        }

        if (apparel && apparel[0]) {
          this.getIntersectObserver().observe(apparel[0]);
        }

        if (shoes && shoes[0]) {
          this.getIntersectObserver().observe(shoes[0]);
        }
      });
    },
    getIntersectObserver(threshold = 1) {
      return new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting === true) {
            const mainGroup = (entries[0].target as HTMLElement).dataset.mainGroup as string;

            this.selectedMainGroup = mainGroup;
          }
        },
        {
          root: (this.$refs['products-models-list'] as HTMLElement[])?.[0],
          threshold,
        },
      );
    },
    scrollMeTo(refName: string) {
      let element = this.$refs[refName] as HTMLElement | HTMLElement[];

      if (!element) {
        return;
      }

      if ((element as HTMLElement[]).length) {
        element = (element as HTMLElement[])[0];
      }

      (element as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      this.selectedMainGroup = refName;
    },
    showProductModelDetailsPopup(productModel: AllowableProductModel) {
      this.showProductModelDetails = productModel;
    },
    closeProductModelDetailsPopup() {
      this.showProductModelDetails = undefined;
    },
    toggleShowProductModelDetailsSelected() {
      if (this.showProductModelDetails) {
        this.selectProductModel(this.showProductModelDetails);
        this.closeProductModelDetailsPopup();
      }
    },
    async fetchAllowableAssortment() {
      this.loading = true;

      this.allowableProductModels = await this.allowableAssortmentService.getAllowableAssortment(
        this.accountId,
        this.orderType as OrderType,
        this.formatDateToIsoStringIgnoringTime(this.requestedDate),
        this.orderNumber,
      );

      if (this.allowableProductModels.length === 0) {
        this.loading = false;

        return this.$snotify.warning(this.$t('matrix.no_assortment', { orderType: this.orderType }));
      }

      this.allowableProductModels.sort((productA, productB) => {
        if (productA.name < productB.name) {
          return -1;
        }

        if (productA.name > productB.name) {
          return 1;
        }

        return 0;
      });

      this.allowableProductModels.forEach((productModel) => {
        if (this.preSelectedProductModelNames.includes(productModel.name)) {
          productModel.selected = true;
        }
      });

      this.loading = false;
      this.observeMainGroups();
      this.emitSelectedProductModels();
    },
    getSearchPlaceholder(): string {
      return this.$t('matrix.product_group_search_placeholder').toString();
    },
    getWidthSearchInput(): string {
      const pixelsPerLetter = 6.3;
      const padding = 56;

      return this.getSearchPlaceholder().length * pixelsPerLetter + padding + 'px';
    },
    getJumpToImageUrl(mainGroup: ProductMainGroups): string {
      return new URL(`/src/assets/img/ico-${mainGroup}.png`, import.meta.url).href;
    },
  },
});
</script>
<style scoped lang="scss">
.products-models-selector {
  margin: 0 auto;

  * {
    scroll-margin-top: 180px;
  }

  @include breakpoint(medium) {
    * {
      scroll-margin-top: 140px;
    }
  }

  @include breakpoint(small) {
    * {
      scroll-margin-top: 270px;
    }
  }

  .products-models-selector-cnt {
    .navbar-background {
      @include navbar-background;
      top: 83px;

      @include breakpoint(medium) {
        top: 63px;
      }

      @include breakpoint(small) {
        top: 180px;
      }

      .no-prooducts-found-message {
        padding: 0 0 20px 25px;
        text-align: left;
      }
    }

    .jump-to-menu {
      @include common-container;
      @include container-background;
      height: fit-content;
      border-radius: 0;
      margin: 0 auto;

      .jump-to-main-groups {
        display: flex;
        align-items: center;
        justify-content: left;
        gap: 12px;
        flex-wrap: wrap;

        button.jump-to {
          border-radius: 21px;
          height: 42px;
          box-sizing: border-box;
          font-size: 14px;
          background-color: transparent;
          border: 1px solid var(--border-color);
          color: var(--primary-text-color);
          background-repeat: no-repeat;
          padding-left: 40px;
          margin-bottom: 10px;
          text-align: left;
          background-position: 4px;
          cursor: pointer;
          padding: 0 20px 0 40px;
          margin: 0;
          flex-shrink: 0;
          background-size: 32px;

          &.selected {
            outline: 2px solid var(--new-primary-color);
            background-color: var(--bg-remark-selected-color);
            padding-left: 39px;
            background-position-x: 3px;
          }
        }

        .search-input {
          border-radius: 21px;
          height: 42px;
          padding: 0 16px 0 40px;
          font-size: 14px;
          background: url('@/assets/img/ico-search.svg') 10px no-repeat;
          stroke: var(--primary-text-color);
          color: var(--primary-text-color);
          min-width: 100px;
          width: auto !important;
          max-width: 510px;
          flex: 1;
          @include ico-search('black');
        }
      }
    }

    .products-models-list {
      @include common-container;
      padding: 20px;

      .main-group-section {
        padding-top: 45px; // this will allow you to scroll 15px below your 30px header
        margin-top: -45px;

        .section-actions {
          display: flex;
          flex-wrap: wrap;
          padding: 16.5px 0;
          gap: 10px;
          align-items: center;

          .section-title {
            text-align: left;
            font-size: 1.25em;
            line-height: 1.5em;
            flex: 1;
            text-wrap: nowrap;
          }

          .section-buttons {
            display: flex;
            align-items: center;
            gap: 20px;

            button {
              margin-right: 20px;
              &:last-child {
                margin-right: 0px;
              }
            }
          }
        }
      }

      .product-model-cards {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
        justify-content: space-between;
        gap: 20px;

        @include breakpoint(medium) {
          grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
        }

        .product-model-card {
          width: 100%;
          aspect-ratio: 172.29 / 190;
          box-sizing: border-box;
          background-color: var(--primary-bg-color);
          color: var(--primary-text-color);
          border-radius: 4px;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          align-items: center;
          cursor: pointer;
          position: relative;

          &.selected {
            outline: 2px solid var(--new-primary-color);
          }

          @media (hover) {
            &:focus {
              border: 2px solid var(--bg-remark-selected-color);
            }

            &:hover {
              background-color: var(--bg-remark-selected-color);
            }

            &:hover,
            &:focus {
              .expand-image-btn {
                display: block;
              }
            }
          }

          .card-top {
            width: 100%;
            height: 63%;

            .img {
              height: 100%;
              width: 100%;
              background-size: contain;
              background-repeat: no-repeat;
              background-position: center center;
            }
          }

          .card-bottom {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px;
            width: 100%;
            height: 42px;

            span {
              text-align: left;
              font-size: 0.875em;
            }
          }

          .expand-image-btn {
            position: absolute;
            top: 9px;
            right: 12px;
            width: 24px;
            height: 24px;
            @include icon-image('@/assets/img/icons/expand.svg', var(--primary-text-color));
            @media (hover) {
              display: none;
            }
          }
        }
      }
    }
  }

  .product-groups-cnt {
    background-color: var(--primary-bg-color);
  }

  .popup-new:deep() {
    .popup {
      padding: 0;
      border-radius: 0;
      .popup-content {
        background-color: var(--bg-page-color);
      }
    }
  }
}

.dark .products-models-selector .products-models-selector-cnt .jump-to-menu .search-input {
  @include ico-search('white');
}

.dark
  .products-models-selector
  .products-models-selector-cnt
  .products-models-list
  .product-model-cards
  article.product-model-card {
  background-color: var(--bg-remark-selected-color);
}
</style>
